import ChevronLeftOutlinedIcon from '@mui/icons-material/ChevronLeftOutlined';
import ChevronRightOutlinedIcon from '@mui/icons-material/ChevronRightOutlined';
import FilterListOutlinedIcon from '@mui/icons-material/FilterListOutlined';
import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { StoreDateTime } from '../../../components/StoreDateTime';
import { useAuthContext } from '../../../context/AuthContext';
import { useNotificationContext } from '../../../context/NotificationContext';
import { ImageZoomModal } from '../../../pages/embeddings/components/ImageZoomModal';
import { ISSUE_REASONS } from '../../../types';
import { FORGE_CHAIN_OPTIONS_ASDA_FIRST as CHAIN_OPTIONS } from '../../config/chains';
import { ForgeApiService } from '../../services/ForgeApiService';
import { ReportedIssueRow, ReportedIssuesResponse } from '../../types';

const PAGE_LIMIT = 50;

const REASON_LABELS: Record<string, string> = Object.fromEntries(
  ISSUE_REASONS.map((r) => [r.value, r.label])
);

const SOURCE_LABELS: Record<string, string> = {
  ai_audit: 'AI Audit',
  control_store: 'Control Store',
  oos_correction: 'OOS Correction',
  forge_remote_dc: 'Forge Remote DC',
  data_collection: 'Data Collection',
  field_tool: 'Field Tool',
};

function labelFor(map: Record<string, string>, value: string): string {
  return map[value] ?? value;
}

function controlImagePath(issue: ReportedIssueRow): string | null {
  if (!issue.storeName || !issue.deviceName) return null;
  return `/stores/${encodeURIComponent(issue.storeName)}/${encodeURIComponent(
    issue.deviceName
  )}?image_id=${encodeURIComponent(issue.imageId)}`;
}

function thumbUrl(imageUrl: string, size = 80): string {
  const separator = imageUrl.includes('?') ? '&' : '?';
  return `${imageUrl}${separator}thumb=${size}`;
}

function formatNumber(value?: number | null): string {
  return (value ?? 0).toLocaleString();
}

function visibleRange(skip: number, limit: number, total: number): string {
  if (!total) return '0';
  const start = skip + 1;
  const end = Math.min(skip + limit, total);
  return `${formatNumber(start)}-${formatNumber(end)}`;
}

export const ReportedIssuesDashboard: React.FC = () => {
  const { token, canSeeDashboards } = useAuthContext();
  const { showErrorToast } = useNotificationContext();

  const [response, setResponse] = useState<ReportedIssuesResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [reason, setReason] = useState<string>('all');
  const [source, setSource] = useState<string>('all');
  const [chainId, setChainId] = useState<number | 'all'>('all');
  const [imageIdInput, setImageIdInput] = useState('');
  const [imageId, setImageId] = useState('');
  const [skip, setSkip] = useState(0);

  const [zoomImageUrl, setZoomImageUrl] = useState<string | null>(null);

  const loadSequence = useRef(0);

  const loadIssues = useCallback(async () => {
    if (!token || !canSeeDashboards) return;
    const sequence = ++loadSequence.current;
    setIsLoading(true);
    setLoadError(null);
    try {
      const result = await ForgeApiService.fetchReportedIssues(token, {
        reason: reason === 'all' ? null : reason,
        source: source === 'all' ? null : source,
        chainId: chainId === 'all' ? null : chainId,
        imageId: imageId || null,
        skip,
        limit: PAGE_LIMIT,
      });
      if (sequence !== loadSequence.current) return;
      setResponse(result);
    } catch (error) {
      if (sequence !== loadSequence.current) return;
      const message = error instanceof Error ? error.message : 'Error loading reported issues';
      setLoadError(message);
      showErrorToast(message);
    } finally {
      if (sequence === loadSequence.current) setIsLoading(false);
    }
  }, [token, canSeeDashboards, reason, source, chainId, imageId, skip, showErrorToast]);

  useEffect(() => {
    void loadIssues();
  }, [loadIssues]);

  const total = response?.total ?? 0;
  const items = response?.items ?? [];
  const hasNext = skip + PAGE_LIMIT < total;
  const page = Math.floor(skip / PAGE_LIMIT) + 1;

  const reasonOptions = useMemo(() => ISSUE_REASONS, []);

  const handleImageIdSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    setSkip(0);
    setImageId(imageIdInput.trim());
  };

  if (!canSeeDashboards) {
    return (
      <div className="page-content page-content--wide dg-dashboard-page">
        <section className="forge-card dg-dashboard-hero">
          <div className="dg-dashboard-title">Reported Issues</div>
          <div className="dg-empty-state">You do not have access to this page.</div>
        </section>
      </div>
    );
  }

  return (
    <div className="page-content page-content--wide dg-dashboard-page">
      <section className="forge-card dg-dashboard-hero">
        <div className="dg-hero-main">
          <div>
            <div className="dg-dashboard-title">Reported Issues</div>
            <div className="dg-hero-metrics">
              <span>
                <strong>{formatNumber(total)}</strong>
                reported
              </span>
            </div>
          </div>
        </div>
        <div className="dg-filter-row">
          <div className="dg-filter-group">
            <FilterListOutlinedIcon />
            <select
              aria-label="Reason filter"
              value={reason}
              onChange={(event) => {
                setSkip(0);
                setReason(event.target.value);
              }}
            >
              <option value="all">All reasons</option>
              {reasonOptions.map((r) => (
                <option key={r.value} value={r.value}>
                  {r.label}
                </option>
              ))}
            </select>
          </div>
          <div className="dg-filter-group">
            <FilterListOutlinedIcon />
            <select
              aria-label="Source filter"
              value={source}
              onChange={(event) => {
                setSkip(0);
                setSource(event.target.value);
              }}
            >
              <option value="all">All sources</option>
              {Object.entries(SOURCE_LABELS).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </div>
          <div className="dg-filter-group">
            <FilterListOutlinedIcon />
            <select
              aria-label="Chain filter"
              value={chainId}
              onChange={(event) => {
                setSkip(0);
                setChainId(event.target.value === 'all' ? 'all' : Number(event.target.value));
              }}
            >
              <option value="all">All chains</option>
              {CHAIN_OPTIONS.map((chain) => (
                <option key={chain.id} value={chain.id}>
                  {chain.label}
                </option>
              ))}
            </select>
          </div>
          <form className="dg-search dg-search--compact" onSubmit={handleImageIdSubmit}>
            <SearchOutlinedIcon />
            <input
              type="search"
              aria-label="Filter by image ID"
              placeholder="Image ID"
              value={imageIdInput}
              onChange={(event) => setImageIdInput(event.target.value)}
            />
          </form>
          <button
            type="button"
            className="dg-secondary-button"
            disabled={isLoading}
            onClick={() => void loadIssues()}
          >
            <RefreshOutlinedIcon className={isLoading ? 'is-spinning' : ''} />
            Refresh view
          </button>
        </div>
      </section>

      <section className="dg-dashboard-grid">
        <div className="forge-card dg-list-panel">
          <div className="dg-list-header">
            <div>
              <div className="dg-panel-title">Reported Issues</div>
              <div className="dg-list-count">
                Showing {visibleRange(skip, PAGE_LIMIT, total)} of {formatNumber(total)}
              </div>
            </div>
            <div className="dg-pagination">
              <button
                type="button"
                className="dg-icon-button"
                aria-label="Previous page"
                disabled={skip <= 0 || isLoading}
                onClick={() => setSkip((current) => Math.max(0, current - PAGE_LIMIT))}
              >
                <ChevronLeftOutlinedIcon />
              </button>
              <span>Page {formatNumber(page)}</span>
              <button
                type="button"
                className="dg-icon-button"
                aria-label="Next page"
                disabled={!hasNext || isLoading}
                onClick={() => setSkip((current) => current + PAGE_LIMIT)}
              >
                <ChevronRightOutlinedIcon />
              </button>
            </div>
          </div>
          <div className="dg-group-list">
            {isLoading ? (
              <div className="dg-loading-state">Loading reported issues...</div>
            ) : loadError ? (
              <div className="dg-empty-state">
                <strong>Reported issues could not be loaded</strong>
                <span>{loadError}</span>
                <button
                  type="button"
                  className="dg-secondary-button"
                  onClick={() => void loadIssues()}
                >
                  Retry
                </button>
              </div>
            ) : items.length ? (
              <div className="dg-group-table-scroll">
                <table
                  className="dg-group-table dg-reported-issues-table"
                  aria-label="Reported issues table"
                >
                  <thead>
                    <tr>
                      <th>Image</th>
                      <th>Preview</th>
                      <th>Reason</th>
                      <th>Note</th>
                      <th>Source</th>
                      <th>Store</th>
                      <th>Camera</th>
                      <th>Mod</th>
                      <th>Reported by</th>
                      <th>Detected at</th>
                      <th>Reported at</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((issue) => (
                      <ReportedIssueTableRow
                        key={issue.id}
                        issue={issue}
                        onZoom={setZoomImageUrl}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="dg-empty-state">No reported issues match this view.</div>
            )}
          </div>
        </div>
      </section>

      <ImageZoomModal
        isOpen={zoomImageUrl != null}
        imageSrc={zoomImageUrl ?? ''}
        onClose={() => setZoomImageUrl(null)}
      />
    </div>
  );
};

const ReportedIssueTableRow: React.FC<{
  issue: ReportedIssueRow;
  onZoom: (url: string) => void;
}> = ({ issue, onZoom }) => {
  const controlPath = controlImagePath(issue);
  return (
    <tr>
      <td>
        {controlPath ? (
          <Link
            className="dg-link underline"
            to={controlPath}
            target="_blank"
            rel="noopener noreferrer"
            title={issue.imageId}
          >
            {issue.imageId}
          </Link>
        ) : (
          <span title="Store/device unavailable">{issue.imageId}</span>
        )}
      </td>
      <td>
        <img
          src={thumbUrl(issue.imageUrl)}
          alt={`Reported ${issue.imageId}`}
          loading="lazy"
          className="cursor-zoom-in rounded"
          style={{ height: 60, width: 'auto', objectFit: 'cover' }}
          onClick={() => onZoom(issue.imageUrl)}
        />
      </td>
      <td>{labelFor(REASON_LABELS, issue.reason)}</td>
      <td
        className="dg-note-cell"
        title={issue.note ?? undefined}
        style={{
          maxWidth: 170,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {issue.note ?? '-'}
      </td>
      <td>{labelFor(SOURCE_LABELS, issue.source)}</td>
      <td>{issue.storeName ?? (issue.storeId != null ? `#${issue.storeId}` : '-')}</td>
      <td>{issue.deviceName ?? (issue.cameraId != null ? `#${issue.cameraId}` : '-')}</td>
      <td>{issue.modTag ?? '-'}</td>
      <td
        title={issue.reportedByEmail ?? undefined}
        style={{
          maxWidth: 150,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {issue.reportedByEmail ?? '-'}
      </td>
      <td>
        {issue.detectedAt ? (
          <StoreDateTime value={issue.detectedAt} storeTimeZone={undefined} />
        ) : (
          '-'
        )}
      </td>
      <td>
        <StoreDateTime value={issue.createdAt} storeTimeZone={undefined} />
      </td>
    </tr>
  );
};
